import { CATEGORIES, dayCategoryTotals } from '@wiztime/core';
import { useTranslation } from 'react-i18next';
import { CATEGORY_COLOR } from '../../../lib/categoryColor';
import { formatHours } from '../../../lib/format';
import { projectHex } from '../../../lib/projectColor';
import { Card } from '../../../ui/Card';
import { useDayData } from '../DayDataContext';
import styles from './Cards.module.css';

/** Confirmed minutes of the day by category and project; drafts are not counted. */
export function DaySummaryCard() {
  const { t } = useTranslation();
  const data = useDayData();
  const totals = dayCategoryTotals(data.blocks, data.projects.all);
  const rows = Object.entries(totals.byProject)
    .map(([projectId, minutes]) => ({ project: data.projects.byId.get(projectId), minutes }))
    .sort((a, b) => b.minutes - a.minutes);
  const longest = rows[0]?.minutes ?? 1;

  return (
    <Card
      title={t('summary.title')}
      loading={data.blocksLoading || data.projects.loading}
      error={data.blocksError !== null || data.projects.error !== null}
      errorText={t('summary.loadError')}
      onRetry={() => {
        data.retryBlocks();
        data.projects.retry();
      }}
      testId="summary-card"
    >
      {totals.totalMin === 0 ? (
        <p className={styles.empty}>{t('summary.empty')}</p>
      ) : (
        <>
          <div
            className={styles.stack}
            role="img"
            aria-label={t('summary.total', { hours: formatHours(totals.totalMin) })}
          >
            {CATEGORIES.map((category) => (
              <span
                key={category}
                style={{ flexGrow: totals.byCategory[category], background: CATEGORY_COLOR[category] }}
                title={`${t(`category.${category}`)} ${formatHours(totals.byCategory[category])}`}
              />
            ))}
          </div>
          <ul className={styles.bars}>
            {rows.map(({ project, minutes }) => (
              <li key={project?.id ?? minutes} data-testid="summary-row">
                <span className={styles.barLabel}>{project?.data.name ?? t('timeline.unknownProject')}</span>
                <span className={styles.barValue}>{formatHours(minutes)}</span>
                <span className={styles.barTrack}>
                  <span
                    style={{
                      width: `${(minutes / longest) * 100}%`,
                      background:
                        project === undefined ? CATEGORY_COLOR.rest : projectHex(project, data.projects.byId),
                    }}
                  />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
